import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Avatar, Card, Typography, Space, Tag, theme, Input, Button, message, Alert } from 'antd';
import { CalendarOutlined, HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import WorkspaceCommentCard from './WorkspaceCommentCard';
import WorkspaceFileCard from './WorkspaceFileCard';
import WorkspaceThreadReplies from './WorkspaceThreadReplies';
import {
  WORKSPACE_EVENT_TYPES,
  WORKSPACE_EVENT_STRUCTURED_REQUEST_TYPES,
  WORKSPACE_EVENT_STATUS_TERMINAL,
  workspaceEventReplyBlocked,
  workspaceEventTypeLabel,
  workspaceEventStatusLabel,
  humanizeFieldKey,
  humanizeValue,
} from './workspaceEventConstants';

const STATUS_OUTCOME_FEEDBACK = {
  approved: {
    type: 'success',
    title: 'Request approved.',
    message: 'Your submission was approved.',
  },
  resolved: {
    type: 'info',
    title: 'Request resolved.',
    message: 'This request has been marked resolved.',
  },
  rejected: {
    type: 'warning',
    title: 'Request rejected.',
    message: 'This submission was not accepted.',
  },
};

function StatusOutcomeBanner({ status, comment, at }) {
  const key = String(status || '').toLowerCase().trim();
  const fb = STATUS_OUTCOME_FEEDBACK[key];
  if (!fb) return null;
  const description = comment && String(comment).trim() ? String(comment).trim() : fb.message;
  const whenLabel = at ? dayjs(at).format('h:mm A') : null;
  return (
    <div className="workspace-customer-status-outcome">
      <Text className="workspace-customer-status-outcome__footer mb-2">
        {fb.title}{whenLabel ? ` • ${whenLabel}` : ''}
      </Text>
      <Alert type={fb.type} message={description} />
    </div>
  );
}

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;

function stripHtml(html) {
  return html ? String(html).replace(/<[^>]*>/g, '') : '';
}

function hasWorkspaceEventMainBody(html) {
  return Boolean(html && stripHtml(html).trim());
}

function mergeWorkspaceEventThreadItems(replies, notes) {
  const r = Array.isArray(replies) ? replies : [];
  const n = Array.isArray(notes) ? notes : [];
  return [...r, ...n].sort((a, b) => {
    const ta = a?.created_at ? dayjs(a.created_at).valueOf() : 0;
    const tb = b?.created_at ? dayjs(b.created_at).valueOf() : 0;
    return ta - tb;
  });
}

const TYPE_TAG_COLOR = {
  [WORKSPACE_EVENT_TYPES.MESSAGE]: 'default',
  [WORKSPACE_EVENT_TYPES.UPLOAD]: 'processing',
  [WORKSPACE_EVENT_TYPES.REVISION_REQUEST]: 'warning',
  [WORKSPACE_EVENT_TYPES.DOCUMENT_REQUEST]: 'warning',
  [WORKSPACE_EVENT_TYPES.APPROVAL]: 'gold',
  [WORKSPACE_EVENT_TYPES.CONFIRMATION_REQUEST]: 'cyan',
  [WORKSPACE_EVENT_TYPES.SYSTEM]: 'default',
  [WORKSPACE_EVENT_TYPES.STATUS_CHANGE]: 'blue',
};

function operationalSummary(event, eventType) {
  const meta = event.metadata && typeof event.metadata === 'object' ? event.metadata : {};
  if (eventType === WORKSPACE_EVENT_TYPES.STATUS_CHANGE && meta.field) {
    return `${humanizeFieldKey(meta.field)} updated from ${humanizeValue(meta.from)} to ${humanizeValue(meta.to)}`;
  }
  if (eventType === WORKSPACE_EVENT_TYPES.SYSTEM && meta.message) {
    return String(meta.message);
  }
  if (eventType === WORKSPACE_EVENT_TYPES.SYSTEM) {
    return 'Update from your business';
  }
  return null;
}

function parseStructuredRequestFields(metadata) {
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  const note = meta.note != null && String(meta.note).trim() ? String(meta.note).trim() : null;
  const doc =
    meta.document_label != null && String(meta.document_label).trim()
      ? String(meta.document_label).trim()
      : null;
  const dueRaw = meta.due_at != null && String(meta.due_at).trim() ? String(meta.due_at).trim() : null;
  const dueDisplay = dueRaw && dayjs(dueRaw).isValid() ? dayjs(dueRaw).format('MMM D, YYYY') : dueRaw;
  return { note, doc, dueDisplay };
}

function StructuredRequestMetadata({ metadata }) {
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  const note = meta.note != null && String(meta.note).trim() ? String(meta.note).trim() : null;
  const doc =
    meta.document_label != null && String(meta.document_label).trim()
      ? String(meta.document_label).trim()
      : null;
  const due = meta.due_at != null && String(meta.due_at).trim() ? String(meta.due_at).trim() : null;
  if (!note && !doc && !due) return null;
  return (
    <Space orientation="vertical" size="small" style={{ width: '100%' }}>
      {doc ? (
        <Text style={{ fontSize: 13 }}>
          <Text strong>Requested: </Text>
          {doc}
        </Text>
      ) : null}
      {due ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Due {dayjs(due).isValid() ? dayjs(due).format('MMM D, YYYY') : due}
        </Text>
      ) : null}
      {note ? <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{note}</Text> : null}
    </Space>
  );
}

function TimelineStructuredBody({ metadata, eventType, token }) {
  const { note, doc, dueDisplay } = parseStructuredRequestFields(metadata);
  const accent =
    eventType === WORKSPACE_EVENT_TYPES.APPROVAL
      ? token.colorSuccess
      : eventType === WORKSPACE_EVENT_TYPES.DOCUMENT_REQUEST
        ? token.colorInfo
        : token.colorWarning;
  const cardClass =
    eventType === WORKSPACE_EVENT_TYPES.REVISION_REQUEST
      ? 'workspace-admin-event--revision'
      : 'workspace-admin-event--neutral';
  return (
    <Card className={cardClass} size="small">
      {dueDisplay ? (
        <div className="mb-2">
          <CalendarOutlined className="me-2" style={{ color: accent, fontSize: 16 }} />
          <Text strong style={{ color: accent, fontSize: 14 }}>
            Due by {dueDisplay}
          </Text>
        </div>
      ) : null}
      {doc ? (
        <Paragraph style={{ marginBottom: note ? token.marginSM : 0, marginTop: 0 }}>
          <Text strong>Requested document: </Text>
          {doc}
        </Paragraph>
      ) : null}
      {note ? (
        <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.65 }}>
          {note}
        </Paragraph>
      ) : null}
      {!note && !doc ? <Text type="secondary">No additional details.</Text> : null}
    </Card>
  );
}

function actorDisplayName(event, mainCommentRows, media) {
  const c0 = mainCommentRows[0];
  const m0 = media[0];
  const creator = c0?.extra?.creator || m0?.extra?.creator;
  return (
    creator?.extra?.full_name ||
    [creator?.first_name, creator?.last_name].filter(Boolean).join(' ').trim() ||
    c0?.extra?.portal_author?.full_name ||
    m0?.extra?.portal_uploader?.full_name ||
    event.extra?.creator?.extra?.full_name ||
    [event.extra?.creator?.first_name, event.extra?.creator?.last_name].filter(Boolean).join(' ').trim() ||
    event.extra?.portal_author?.full_name ||
    'Team'
  );
}

function actorProfileUrl(event, mainCommentRows, media) {
  const c0 = mainCommentRows[0];
  const m0 = media[0];
  return (
    c0?.extra?.creator?.extra?.profile_image ||
    m0?.extra?.creator?.extra?.profile_image ||
    event.extra?.creator?.extra?.profile_image ||
    null
  );
}

function actorInitial(name) {
  const s = String(name || '?').trim();
  return s ? s.charAt(0).toUpperCase() : '?';
}

function activityVerbPhrase(eventType, opts = {}) {
  const fileCount = Number(opts.fileCount) || 0;
  switch (eventType) {
    case WORKSPACE_EVENT_TYPES.MESSAGE:
      return 'commented';
    case WORKSPACE_EVENT_TYPES.UPLOAD:
      return fileCount <= 1 ? 'uploaded a file' : `uploaded ${fileCount} files`;
    case WORKSPACE_EVENT_TYPES.REVISION_REQUEST:
      return 'requested a revision';
    case WORKSPACE_EVENT_TYPES.DOCUMENT_REQUEST:
      return 'requested a document';
    case WORKSPACE_EVENT_TYPES.CONFIRMATION_REQUEST:
      return 'requested confirmation';
    case WORKSPACE_EVENT_TYPES.APPROVAL:
      return 'requested approval';
    case WORKSPACE_EVENT_TYPES.STATUS_CHANGE:
      return 'updated status';
    case WORKSPACE_EVENT_TYPES.SYSTEM:
      return 'posted an update';
    default:
      return 'posted';
  }
}

/** Customer portal: workspace event card.
 * layout="default" → card embed (sidebar / card panels)
 * layout="timeline" → workspace timeline design matching admin workspace
 */
const WorkspaceEventCard = ({
  event,
  onPreviewFile,
  onWorkspaceEventReply = null,
  layout = 'default',
  onOpenReply = null,
  onMarkRead = null,
}) => {
  const { token } = theme.useToken();
  const [replyDraft, setReplyDraft] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const cardRef = useRef(null);

  // Customer sees unread indicator only on admin-created messages (no portal_author = staff-posted).
  // Customer's own messages are never shown as unread to themselves.
  const isAdminCreated = !event.extra?.portal_author;
  const isReadFromServer = !isAdminCreated || event.is_read_by_customer !== false;

  // Local optimistic state — cleared immediately when IntersectionObserver fires so the
  // unread border disappears without waiting for the parent feed to refresh.
  const [localRead, setLocalRead] = useState(isReadFromServer);

  useEffect(() => {
    if (isReadFromServer) setLocalRead(true);
  }, [isReadFromServer]);

  const isRead = localRead;

  useEffect(() => {
    if (isRead || !onMarkRead || !event.id || layout !== 'timeline') return;
    const el = cardRef.current;
    if (!el) return;
    let timer = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Only mark read after 1.5s of continuous visibility so the user can
          // actually see the unread indicator before it disappears.
          timer = setTimeout(() => {
            setLocalRead(true);
            onMarkRead(event.id);
            observer.disconnect();
          }, 1500);
        } else {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [isRead, onMarkRead, event.id, layout]);

  const repliesList = Array.isArray(event.replies) ? event.replies : [];
  const notesList = Array.isArray(event.notes) ? event.notes : [];
  const media = Array.isArray(event.media) ? event.media : [];
  const visibility = event.visibility || 'internal';
  const threadItems = mergeWorkspaceEventThreadItems(repliesList, notesList);
  const mainCommentRows = hasWorkspaceEventMainBody(event.comment)
    ? [
        {
          id: `${event.id}_main`,
          body: event.comment,
          visibility,
          is_internal_note: false,
          created_at: event.created_at,
          extra: event.extra || {},
          replies: [],
        },
      ]
    : [];

  const eventType = event.type || WORKSPACE_EVENT_TYPES.MESSAGE;
  const typeLabel = workspaceEventTypeLabel(eventType);
  const typeColor = TYPE_TAG_COLOR[eventType] || 'default';
  const statusLabel = workspaceEventStatusLabel(event.status);
  const opSummary = operationalSummary(event, eventType);
  const showStructuredMeta = WORKSPACE_EVENT_STRUCTURED_REQUEST_TYPES.includes(eventType);
  const isWorkspace = layout === 'timeline';

  const isOperationalOnly =
    (eventType === WORKSPACE_EVENT_TYPES.STATUS_CHANGE || eventType === WORKSPACE_EVENT_TYPES.SYSTEM) &&
    !mainCommentRows.length &&
    !media.length &&
    !threadItems.length;

  const replyBlocked = workspaceEventReplyBlocked(eventType, event.status);

  const statusOutcomeMeta = event.metadata && typeof event.metadata === 'object' ? event.metadata : {};
  const statusOutcomeComment =
    typeof statusOutcomeMeta.status_change_comment === 'string'
      ? statusOutcomeMeta.status_change_comment.trim() || null
      : null;
  const showStatusOutcome =
    WORKSPACE_EVENT_STRUCTURED_REQUEST_TYPES.includes(eventType) &&
    WORKSPACE_EVENT_STATUS_TERMINAL.includes(
      String(event.status || '').toLowerCase().trim(),
    );

  const handleReplySubmit = async () => {
    const body = String(replyDraft || '').trim();
    if (!body || !onWorkspaceEventReply || !event.id) return;
    setReplySubmitting(true);
    try {
      await onWorkspaceEventReply({ workspaceEventPublicId: event.id, body });
      message.success('Reply sent');
      setReplyDraft('');
      setReplyOpen(false);
    } catch {
      message.error('Could not send reply');
    } finally {
      setReplySubmitting(false);
    }
  };

  // ── Default (card) layout ───────────────────────────────────────────────
  if (!isWorkspace) {
    const replySection =
      onWorkspaceEventReply != null ? (
        replyBlocked ? (
          <Alert
            type="warning"
            title={
              eventType === WORKSPACE_EVENT_TYPES.STATUS_CHANGE ||
              eventType === WORKSPACE_EVENT_TYPES.SYSTEM
                ? 'Replies are not available on this type of update.'
                : 'This request is closed — you cannot add a reply.'
            }
            style={{ marginTop: token.marginXS }}
          />
        ) : (
          <Space
            orientation="vertical"
            size="small"
            style={{ width: '100%', marginTop: token.marginSM }}
          >
            <Input.TextArea
              placeholder="Write a reply…"
              value={replyDraft}
              onChange={(e) => setReplyDraft(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 8 }}
              disabled={replySubmitting}
            />
            <Button
              type="primary"
              size="small"
              loading={replySubmitting}
              onClick={() => void handleReplySubmit()}
            >
              Reply
            </Button>
          </Space>
        )
      ) : null;

    if (isOperationalOnly) {
      return (
        <Card
          size="small"
          className="workspace-feed-card workspace-event-card"
          styles={{ body: { padding: 'var(--ant-padding)' } }}
          style={{
            marginBottom: 'var(--ant-margin-sm)',
            border: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            <Tag color={typeColor}>{typeLabel}</Tag>
            {statusLabel ? <Tag color="processing">{statusLabel}</Tag> : null}
            <Text style={{ fontSize: 13 }}>{opSummary}</Text>
            {event.created_at ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(event.created_at).fromNow()}
              </Text>
            ) : null}
          </Space>
        </Card>
      );
    }

    return (
      <>
        <Card
          size="small"
          className="workspace-feed-card workspace-event-card"
          styles={{ body: { padding: 'var(--ant-padding)' } }}
          style={{
            marginBottom: replySection ? 0 : 'var(--ant-margin-sm)',
            border: '1px solid var(--varexe-border-color)',
          }}
        >
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            <Space wrap size="small">
              <Tag color={typeColor}>{typeLabel}</Tag>
              {statusLabel ? <Tag color="processing">{statusLabel}</Tag> : null}
            </Space>
            {event.created_at ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(event.created_at).fromNow()}
              </Text>
            ) : null}
            {opSummary ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {opSummary}
              </Text>
            ) : null}
            {showStructuredMeta ? <StructuredRequestMetadata metadata={event.metadata} /> : null}
            {mainCommentRows.map((c) => (
              <WorkspaceCommentCard key={c.id} comment={c} onPreviewFile={onPreviewFile} />
            ))}
            {threadItems.map((row) => (
              <div
                key={row.id}
                style={{
                  marginTop: 'var(--ant-margin-xs)',
                  paddingLeft: 'var(--ant-padding)',
                  borderLeft: `2px solid ${token.colorBorderSecondary}`,
                }}
              >
                <WorkspaceCommentCard comment={row} embedded onPreviewFile={onPreviewFile} />
              </div>
            ))}
            {media.map((m) => (
              <WorkspaceFileCard key={m.id} media={m} onPreview={() => onPreviewFile?.(m)} />
            ))}
          </Space>
        </Card>
        {showStatusOutcome ? (
          <StatusOutcomeBanner
            status={event.status}
            comment={statusOutcomeComment}
            at={event.updated_at}
          />
        ) : null}
        {replySection ? (
          <div style={{ marginBottom: 'var(--ant-margin-sm)', marginTop: token.marginSM }}>
            {replySection}
          </div>
        ) : null}
      </>
    );
  }

  // ── Timeline (workspace) layout — matches admin workspace design ─────────
  const headline = actorDisplayName(event, mainCommentRows, media);
  const profileUrl = actorProfileUrl(event, mainCommentRows, media);
  const adminCardSurfaceSuffix =
    eventType === WORKSPACE_EVENT_TYPES.REVISION_REQUEST ? 'revision' : 'neutral';
  const cardShellClass = `workspace-feed-card workspace-admin-event workspace-admin-event--${adminCardSurfaceSuffix}`;

  const actorHeader = (
    <div className="workspace-admin-event__header-row mb-2">
      <Avatar
        size={20}
        src={profileUrl || undefined}
        style={{
          flexShrink: 0,
          backgroundColor: token.colorFillSecondary,
          color: token.colorTextSecondary,
        }}
      >
        {!profileUrl ? actorInitial(headline) : null}
      </Avatar>
      <div className="workspace-admin-event__header-text">
        <Text className="workspace-admin-event__actor">{headline}</Text>
        <Text type="secondary" className="workspace-admin-event__verb">
          {activityVerbPhrase(eventType, { fileCount: media.length })}
        </Text>
        {event.created_at ? (
          <Text type="secondary" className="workspace-admin-event__time">
            {dayjs(event.created_at).format('h:mm A')}
          </Text>
        ) : null}
      </div>
    </div>
  );

  const timelineReplySection =
    onOpenReply != null && !replyBlocked ? (
      <div className="workspace-admin-below-card">
        <Space size="small" wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button
            color="primary"
            variant="filled"
            size="small"
            onClick={() => onOpenReply?.({ workspaceEventPublicId: event.id, actorName: headline })}
          >
            Reply
          </Button>
        </Space>
      </div>
    ) : null;

  if (isOperationalOnly) {
    return (
      <Fragment>
        {actorHeader}
        <div ref={cardRef} className={`mb-6${!isRead ? ' workspace-event-card--unread' : ''}`}>
          <Card
            size="small"
            className={cardShellClass}
            styles={{ body: { padding: 'var(--ant-padding-lg)', minWidth: 0, overflow: 'hidden' } }}
          >
            <Space orientation="vertical" size="small" style={{ width: '100%' }}>
              <Space wrap size="small">
                <Tag color={typeColor}>{typeLabel}</Tag>
                {statusLabel ? <Tag color="processing">{statusLabel}</Tag> : null}
              </Space>
              <Space align="start" size="small">
                <HistoryOutlined
                  style={{ color: token.colorTextSecondary, fontSize: 16, marginTop: 2 }}
                />
                <Text style={{ fontSize: 14 }}>{opSummary}</Text>
              </Space>
              {event.created_at ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {dayjs(event.created_at).format('MMM D, YYYY · h:mm A')} ·{' '}
                  {dayjs(event.created_at).fromNow()}
                </Text>
              ) : null}
            </Space>
          </Card>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {actorHeader}
      <div ref={cardRef} className={`mb-6${!isRead ? ' workspace-event-card--unread' : ''}`}>
        <Card
          size="small"
          className={cardShellClass}
          styles={{ body: { minWidth: 0, overflow: 'hidden' } }}
        >
          <Space orientation="vertical" size="small" style={{ width: '100%', minWidth: 0 }}>
            {!showStructuredMeta && opSummary ? (
              <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                {opSummary}
              </Text>
            ) : null}
            {showStructuredMeta ? (
              <TimelineStructuredBody
                metadata={event.metadata}
                eventType={eventType}
                token={token}
              />
            ) : null}
            {mainCommentRows.map((c) => (
              <Paragraph
                key={c.id}
                style={{ marginBottom: 0, whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.65 }}
              >
                {stripHtml(c.body)}
              </Paragraph>
            ))}
            {media.length > 0 ? (
              <div
                className="workspace-admin-event-media-grid"
                style={{ marginTop: mainCommentRows.length ? 'var(--ant-padding-xs)' : 0 }}
              >
                {media.map((m) => (
                  <div key={m.id} className="workspace-admin-attachment">
                    <WorkspaceFileCard media={m} onPreview={() => onPreviewFile?.(m)} />
                  </div>
                ))}
              </div>
            ) : null}
          </Space>
        </Card>
        {threadItems.length > 0 ? (
          <WorkspaceThreadReplies replies={threadItems} onPreviewFile={onPreviewFile} />
        ) : null}
        {showStatusOutcome ? (
          <StatusOutcomeBanner
            status={event.status}
            comment={statusOutcomeComment}
            at={event.updated_at}
          />
        ) : null}
        {timelineReplySection}
      </div>
    </Fragment>
  );
};

export default WorkspaceEventCard;
